import { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Grid, Hidden, LinearProgress } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { useHistory } from 'react-router';
import { Formik } from 'formik';
import { MFSubmitButton, MFTextField } from '../../lib/formik';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { DatePicker } from '../../lib/formik/DatePicker';
import { useDispatch } from 'react-redux';
import { SubHeading } from '../investors/components';
import { addPlan, fundAccess, getPlanDetail, updatePlan } from '../../redux-store/actions/funds';
import {
  accessType,
  addFundPlanType,
  addPlanType,
  ClassPlanProps,
} from '../../redux-store/types/funds';
import MFSelectField from '../../lib/formik/SelectField';
import { Location } from 'history';
import { getFundManagersList, getTrustessList } from '../../redux-store/actions/userManagement';
import { DistributorListType, FundManager, Trustee } from '../../redux-store/types/api-types';
import { addPlanValidation } from '../../utils/schema';
import {
  PlacementFeeApplicable,
  allowOnlyNumbers,
  currencyConversion,
  isFieldApplicable,
  isFieldTypeFixed,
  isFieldTypeVariable,
} from '../../utils/utilityFunctions';
import {
  CurrencyMaster,
  defaultCurrency,
  feeTypeMaster,
  flowTypeMaster,
  investmentPlanTypeMaster,
  yesNoMaster,
} from '../../utils/constant';
import { MultipleSelect } from '../../lib/formik/MultipleSelectField';
import { getDistributorsListForFilters } from '../../redux-store/actions';
import MFCheckbox from '../../lib/formik/Checkbox';
import UseRadioGroup from '../../lib/formik/Radio';

type NewPlanType = addPlanType & {
  isApplicableForDistributor: boolean;
};
const initialValues: NewPlanType = {
  // id: '',
  schemeId: 0,
  planCode: '',
  planDescription: '',
  schemeNature: '',
  registrationNumber: '',
  schemeCategory: '',
  country: '',
  currency: defaultCurrency,
  transactionCurrency: defaultCurrency,
  schemeStartDate: `${new Date()}`,
  lockInPeriod: '',
  lockInExtension: '',
  faceValue: 0,
  fundAddress1: '',
  fundManagerId: null,
  trusteeId: null,
  fundAddress2: '',
  fundAddress3: '',
  inActive: true,
  isActive: true,
  custodian: '',
  setupFee: 0,
  minCommitmentAmount: null,
  maxCommitmentAmount: 0,
  minContributionPercentage: 0,
  managementFee: 0,
  tcLink: '',
  performanceFee: '',
  totalExpense: 0,
  placementFee: null,
  isPlacementFeeApplicable: '',
  hurdleRateOfReturns: '',
  distributorIds: null,
  isApplicableForDirect: false,
  isApplicableForDistributor: false,
  operatingExpenseApplicable: '',
  operatingExpenseInWords: '',
  exitLoadApplicable: '',
  exitLoadInWords: '',
  placementFeeType: '',
  minPlacementFee: null,
  maxPlacementFee: null,
  investmentPlanType: '',
  investmentPlanApplicable: '',
};

export default function AddPlan({
  location,
}: {
  location: Location<{ fundId: string; PlanId: string }>;
}): JSX.Element {
  const history = useHistory();
  const dispatch = useDispatch();
  const [fundManager, setFundManager] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [planInitial, setPlanInitial] = useState(initialValues);
  const [access, setAccess] = useState(false);
  const { fundId, PlanId } = location.state || {};
  const [trustee, setTrustee] = useState<any[]>([]);
  const [distributorList, setDistributorList] = useState<DistributorListType[]>([]);

  useEffect(() => {
    let isComponentAlive = true;
    (async function () {
      try {
        setLoading(true);
        const trusteeList = (await dispatch(getTrustessList())) as unknown as Trustee[];
        const fundManagerList = (await dispatch(getFundManagersList())) as unknown as FundManager[];
        const { fundSetupAccess } = (await dispatch(fundAccess())) as unknown as accessType;
        const distributorLists = (await dispatch(
          getDistributorsListForFilters()
        )) as unknown as DistributorListType[];
        setAccess(fundSetupAccess);
        setFundManager(fundManagerList);
        setTrustee(trusteeList);
        setDistributorList(distributorLists);
        if (PlanId) {
          const GetFundDetail = (await dispatch(
            getPlanDetail(PlanId)
          )) as unknown as ClassPlanProps;
          const {
            planCode,
            schemeId,
            planDescription,
            schemeNature,
            registrationNumber,
            schemeCategory,
            country,
            currency,
            transactionCurrency,
            schemeStartDate,
            lockInPeriod,
            lockInExtension,
            inActive,
            faceValue,
            fundAddress1,
            fundAddress2,
            fundAddress3,
            isActive,
            custodian,
            setupFee,
            minCommitmentAmount,
            maxCommitmentAmount,
            minContributionPercentage,
            managementFee,
            fundManagerId,
            trusteeId,
            tcLink,
            performanceFee,
            totalExpense,
            placementFee = null,
            isPlacementFeeApplicable,
            hurdleRateOfReturns,
            distributorDetails,
            isApplicableForDirect,
            operatingExpenseApplicable = '',
            operatingExpenseInWords = '',
            exitLoadApplicable = '',
            exitLoadInWords = '',
            placementFeeType = '',
            minPlacementFee = null,
            maxPlacementFee = null,
            investmentPlanType = '',
            investmentPlanApplicable = '',
          } = GetFundDetail || {};

          setPlanInitial({
            ...planInitial,
            planCode,
            schemeId,
            planDescription,
            schemeNature,
            registrationNumber,
            schemeCategory,
            country,
            currency: currency?.toLowerCase() || defaultCurrency,
            transactionCurrency,
            schemeStartDate,
            lockInPeriod,
            lockInExtension,
            inActive,
            faceValue,
            fundAddress1,
            fundAddress2,
            fundAddress3,
            isActive,
            custodian,
            setupFee,
            minCommitmentAmount,
            maxCommitmentAmount,
            minContributionPercentage,
            managementFee,
            totalExpense,
            placementFee,
            isPlacementFeeApplicable,
            fundManagerId,
            trusteeId,
            tcLink,
            performanceFee,
            hurdleRateOfReturns,
            distributorIds: distributorDetails.distributorIds || null,
            isApplicableForDirect: isApplicableForDirect || false,
            isApplicableForDistributor: distributorDetails.distributorIds.length > 0 ? true : false,
            operatingExpenseApplicable,
            operatingExpenseInWords,
            exitLoadApplicable,
            exitLoadInWords,
            placementFeeType,
            minPlacementFee,
            maxPlacementFee,
            investmentPlanType,
            investmentPlanApplicable,
          });
        }
        if (!isComponentAlive) return;
        setLoading(false);
      } catch (e) {
        console.error((e as Error).message);
      } finally {
        if (isComponentAlive) {
          setLoading(false);
        }
      }
    })();
    return () => {
      isComponentAlive = false;
    };
  }, []);

  const onSubmit = async (values: NewPlanType) => {
    try {
      const {
        planCode,
        schemeId,
        planDescription,
        schemeNature,
        registrationNumber,
        schemeCategory,
        country,
        currency,
        transactionCurrency,
        schemeStartDate,
        lockInPeriod,
        lockInExtension,
        inActive,
        faceValue,
        fundAddress1,
        fundAddress2,
        fundAddress3,
        isActive,
        custodian,
        setupFee,
        minCommitmentAmount,
        maxCommitmentAmount,
        minContributionPercentage,
        managementFee,
        totalExpense,
        placementFee = null,
        isPlacementFeeApplicable,
        fundManagerId,
        trusteeId,
        tcLink,
        performanceFee,
        hurdleRateOfReturns,
        distributorIds,
        isApplicableForDirect,
        operatingExpenseApplicable = '',
        operatingExpenseInWords = '',
        exitLoadApplicable = '',
        exitLoadInWords = '',
        placementFeeType = '',
        minPlacementFee = null,
        maxPlacementFee = null,
        investmentPlanType = '',
        investmentPlanApplicable = '',
      } = values;

      if (!PlanId) {
        await dispatch(
          addPlan({
            schemeId: fundId,
            planCode,
            planDescription,
            schemeNature,
            registrationNumber,
            schemeCategory,
            country,
            currency,
            transactionCurrency,
            schemeStartDate,
            lockInPeriod,
            lockInExtension,
            fundManagerId,
            trusteeId,
            inActive,
            faceValue,
            fundAddress1,
            fundAddress2,
            fundAddress3,
            isActive,
            custodian,
            setupFee: setupFee ? setupFee : 0,
            minCommitmentAmount,
            maxCommitmentAmount,
            minContributionPercentage: minContributionPercentage ? minContributionPercentage : 0,
            managementFee: managementFee ? managementFee : 0,
            tcLink,
            performanceFee,
            totalExpense,
            placementFee: isFieldApplicable(isPlacementFeeApplicable) ? placementFee : null,
            isPlacementFeeApplicable,
            hurdleRateOfReturns,
            distributorIds,
            isApplicableForDirect,
            operatingExpenseApplicable,
            operatingExpenseInWords: isFieldApplicable(operatingExpenseApplicable)
              ? operatingExpenseInWords
              : null,
            exitLoadApplicable,
            exitLoadInWords: isFieldApplicable(exitLoadApplicable) ? exitLoadInWords : null,
            placementFeeType: isFieldApplicable(isPlacementFeeApplicable) ? placementFeeType : null,
            minPlacementFee:
              isFieldApplicable(isPlacementFeeApplicable) && isFieldTypeVariable(placementFeeType)
                ? minPlacementFee
                : null,
            maxPlacementFee:
              isFieldApplicable(isPlacementFeeApplicable) && isFieldTypeVariable(placementFeeType)
                ? maxPlacementFee
                : null,
            investmentPlanType: isFieldApplicable(investmentPlanApplicable)
              ? investmentPlanType
              : null,
            investmentPlanApplicable,
          })
        );
      } else {
        await dispatch(
          updatePlan(Number(PlanId), {
            schemeId,
            planCode,
            planDescription,
            schemeNature,
            registrationNumber,
            schemeCategory,
            country,
            currency,
            transactionCurrency,
            schemeStartDate,
            lockInPeriod,
            lockInExtension,
            fundManagerId,
            trusteeId,
            inActive,
            faceValue,
            fundAddress1,
            fundAddress2,
            fundAddress3,
            isActive,
            custodian,
            setupFee: setupFee ? setupFee : 0,
            minCommitmentAmount,
            maxCommitmentAmount,
            minContributionPercentage: minContributionPercentage ? minContributionPercentage : 0,
            managementFee: managementFee ? managementFee : 0,
            totalExpense,
            placementFee: isFieldApplicable(isPlacementFeeApplicable) ? placementFee : null,
            isPlacementFeeApplicable,
            tcLink,
            performanceFee,
            hurdleRateOfReturns,
            distributorIds,
            isApplicableForDirect,
            operatingExpenseApplicable,
            operatingExpenseInWords: isFieldApplicable(operatingExpenseApplicable)
              ? operatingExpenseInWords
              : null,
            exitLoadApplicable,
            exitLoadInWords: isFieldApplicable(exitLoadApplicable) ? exitLoadInWords : null,
            placementFeeType: isFieldApplicable(isPlacementFeeApplicable) ? placementFeeType : null,
            minPlacementFee:
              isFieldApplicable(isPlacementFeeApplicable) && isFieldTypeVariable(placementFeeType)
                ? minPlacementFee
                : null,
            maxPlacementFee:
              isFieldApplicable(isPlacementFeeApplicable) && isFieldTypeVariable(placementFeeType)
                ? maxPlacementFee
                : null,
            investmentPlanType: isFieldApplicable(investmentPlanApplicable)
              ? investmentPlanType
              : null,
            investmentPlanApplicable,
          })
        );
      }
      history.push(PlanId ? 'plan-details' : 'view-plans', {
        fundId: fundId,
        PlanId: PlanId,
      });
    } catch (e) {
      console.error((e as Error).message);
    }
  };

  return (
    <>
      <Formik
        initialValues={planInitial}
        onSubmit={onSubmit}
        enableReinitialize={true}
        validationSchema={addPlanValidation}>
        {({ handleSubmit, values, setValues }) => (
          <Box
            sx={{
              bgcolor: 'white',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
              borderRadius: '10px',
              py: { xs: 2, sm: 5 },
              pl: { xs: 0, sm: 5 },
              mt: { xs: 2, sm: 5 },
            }}
            component="form"
            noValidate
            onSubmit={handleSubmit}>
            <Hidden smUp={true}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  sx={{ height: 'fit-content', p: 0 }}
                  onClick={() =>
                    history.push(PlanId ? 'plan-details' : 'view-plans', {
                      fundId: fundId,
                      // planId: PlanId,
                      PlanId: PlanId,
                    })
                  }>
                  <NavigateBeforeIcon fontSize="medium" sx={{ color: 'common.black' }} />
                </IconButton>
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 600,
                    color: '#1C2D47',
                  }}>
                  Back
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  my: 2,
                  py: 2,
                  pl: { xs: 3, sm: 0 },
                  color: 'primary.main',
                  bgcolor: 'rgba(238, 244, 251, 0.5)',
                }}>
                <PersonOutlineOutlinedIcon fontSize="large" />
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 600,
                    pl: 1,
                  }}>
                  Fund Setup
                </Typography>
              </Box>
            </Hidden>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Hidden only="xs">
                <IconButton
                  sx={{ height: 'fit-content', p: 0 }}
                  onClick={() =>
                    history.push(PlanId ? 'plan-details' : 'view-plans', {
                      fundId: fundId,
                      // planId: PlanId,
                      PlanId: PlanId,
                    })
                  }>
                  <NavigateBeforeIcon fontSize="medium" sx={{ color: 'common.black' }} />
                </IconButton>
              </Hidden>
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: '#1C2D47',
                  pl: { xs: 3, sm: 0 },
                }}>
                {PlanId ? 'Edit Plan' : 'Add Plan'}
              </Typography>
            </Box>

            {loading ? (
              <LinearProgress sx={{ mt: 2 }} />
            ) : (
              <>
                <Grid container rowSpacing={1} columnSpacing={4} px={2}>
                  <SubHeading
                    sx={{
                      color: 'common.blue',
                      letterSpacing: 0.5 + 'px',
                      padding: { xs: '10px 10px', sm: '10px 30px' },
                      ml: { xs: 3.5 },
                    }}>
                    {'Plan Details'}
                  </SubHeading>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name="planCode"
                      label="Plan Code *"
                      placeholder="Enter Plan Code"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name="planDescription"
                      label="Plan Description *"
                      placeholder="Enter Plan Description"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name="schemeNature"
                      label="Scheme Nature *"
                      placeholder="Enter Scheme Nature"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name="schemeCategory"
                      label="Scheme Category *"
                      placeholder="Enter Scheme Category"
                    />
                  </Grid>
                  {/* <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name="registrationNumber"
                      label="Registration Number *"
                      placeholder="Enter Registration Number"
                    />
                  </Grid> */}
                  <Grid item xs={12} sm={6} md={4}>
                    <MFSelectField
                      name="fundManagerId"
                      label="Investment Manager Name *"
                      items={fundManager.map((item) => ({
                        key: item.name,
                        value: item.id,
                      }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFSelectField
                      name="trusteeId"
                      label="Trustee Name *"
                      items={trustee.map((item) => ({
                        key: item.name,
                        value: item.id,
                      }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField name="country" label="Country" placeholder="Enter Country" />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFSelectField
                      name="currency"
                      label="Currency"
                      items={Object.keys(CurrencyMaster).map((item) => ({
                        key: CurrencyMaster[item].currency,
                        value: item,
                      }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFSelectField
                      name="transactionCurrency"
                      label="Transaction Currency"
                      items={Object.keys(CurrencyMaster).map((item) => ({
                        key: CurrencyMaster[item].currency,
                        value: item,
                      }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <DatePicker
                      label={'Scheme Start Date'}
                      inputLabelStyles={{
                        transform: 'unset',

                        fontSize: 14,
                        fontWeight: 500,
                        color: 'rgba(0,0,0,0.7)',
                      }}
                      placeholder={'Enter Scheme Start Date'}
                      // maxDate={minDateForContributor()}
                      name={`schemeStartDate`}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name="lockInPeriod"
                      label="Lock In Period(In Year)"
                      placeholder="Enter Lock In Period(In Year)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name="lockInExtension"
                      label="Lock In Extension(In Year)"
                      disabled={!access}
                      placeholder="Enter Lock In Extension(In Year)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name="faceValue"
                      label="Face value"
                      placeholder="Enter Face value"
                      type="number"
                      onKeyDown={(e) => {
                        allowOnlyNumbers(e);
                      }}
                      trimOnBlur={false}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name="fundAddress1"
                      label="Fund Address 1"
                      placeholder="Enter Fund Address 1"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name="fundAddress2"
                      label="Fund Address 2"
                      placeholder="Enter Fund Address 2"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name="fundAddress3"
                      label="Fund Address 3"
                      placeholder="Enter Fund Address 3"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField name="custodian" label="Custodian" placeholder="Enter Custodian" />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name="setupFee"
                      label="Setup Fee"
                      placeholder="Enter Setup Fee"
                      type="number"
                      onKeyDown={(e) => {
                        allowOnlyNumbers(e);
                      }}
                      trimOnBlur={false}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name="minCommitmentAmount"
                      label="Minimum Capital Commitment *"
                      placeholder="Enter Minimum Capital Commitment"
                      type="number"
                      onKeyDown={(e) => {
                        allowOnlyNumbers(e);
                      }}
                      trimOnBlur={false}
                    />
                    {values.minCommitmentAmount != 0 && (
                      <Typography sx={{ fontSize: 13 }}>
                        {currencyConversion(values.minCommitmentAmount, values.currency)}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name="maxCommitmentAmount"
                      label="Maximum Capital Commitment *"
                      placeholder="Enter Maximum Capital Commitment"
                      type="number"
                      onKeyDown={(e) => {
                        allowOnlyNumbers(e);
                      }}
                      trimOnBlur={false}
                    />
                    {values.maxCommitmentAmount != 0 && (
                      <Typography sx={{ fontSize: 13 }}>
                        {currencyConversion(values.maxCommitmentAmount, values.currency)}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name="minContributionPercentage"
                      label="Minimum Contribution Percentage"
                      placeholder="Enter Minimum Contribution Percentage"
                      type="number"
                      onKeyDown={(e) => {
                        allowOnlyNumbers(e);
                      }}
                      trimOnBlur={false}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name="managementFee"
                      label="Management Fee *"
                      placeholder="Enter Management Fee"
                      type="number"
                      onKeyDown={(e) => {
                        allowOnlyNumbers(e);
                      }}
                      trimOnBlur={false}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name={'hurdleRateOfReturns'}
                      label="Transaction Fee %"
                      placeholder="Enter Transaction Fee % "
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name={'performanceFee'}
                      label="Performance Fee %"
                      placeholder="Enter Performance Fee %"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name={'totalExpense'}
                      label="Total Expense % *"
                      placeholder="Enter Total Expense %"
                      onKeyDown={(e) => {
                        allowOnlyNumbers(e);
                      }}
                      type="number"
                      trimOnBlur={false}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} mt={2}>
                    <UseRadioGroup
                      formLabel="Placement Fee Applicable *"
                      name={`isPlacementFeeApplicable`}
                      items={Object.keys(yesNoMaster).map((ele) => ({
                        label: yesNoMaster[ele],
                        value: ele,
                      }))}
                    />
                  </Grid>
                  {isFieldApplicable(values.isPlacementFeeApplicable) && (
                    <>
                      <Grid item xs={12} sm={6} md={4} mt={2}>
                        <UseRadioGroup
                          formLabel="Placement Fee Type *"
                          name={`placementFeeType`}
                          items={Object.keys(feeTypeMaster).map((ele) => ({
                            label: feeTypeMaster[ele],
                            value: ele,
                          }))}
                        />
                      </Grid>
                      {/* {isFieldTypeFixed(values.placementFeeType) && ( */}
                      <Grid item xs={12} sm={6} md={4}>
                        <MFTextField
                          name={'placementFee'}
                          label="Placement Fee % *"
                          placeholder="Enter Placement Fee %"
                          onKeyDown={(e) => {
                            allowOnlyNumbers(e);
                          }}
                          type="number"
                          trimOnBlur={false}
                        />
                      </Grid>
                      {/* )} */}
                      {isFieldTypeVariable(values.placementFeeType) && (
                        <>
                          <Grid item xs={12} sm={6} md={4}>
                            <MFTextField
                              name={'minPlacementFee'}
                              label="Min Placement Fee % *"
                              placeholder="Enter Min Placement Fee %"
                              onKeyDown={(e) => {
                                allowOnlyNumbers(e);
                              }}
                              type="number"
                              trimOnBlur={false}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <MFTextField
                              name={'maxPlacementFee'}
                              label="Max Placement Fee % *"
                              placeholder="Enter Max Placement Fee %"
                              onKeyDown={(e) => {
                                allowOnlyNumbers(e);
                              }}
                              type="number"
                              trimOnBlur={false}
                            />
                          </Grid>
                        </>
                      )}
                    </>
                  )}
                  <Grid item xs={12} sm={6} md={4} mt={2}>
                    <UseRadioGroup
                      formLabel="Operating Expense Applicable *"
                      name={`operatingExpenseApplicable`}
                      items={Object.keys(yesNoMaster).map((ele) => ({
                        label: yesNoMaster[ele],
                        value: ele,
                      }))}
                    />
                  </Grid>
                  {isFieldApplicable(values.operatingExpenseApplicable) && (
                    <Grid item xs={12} sm={6} md={4}>
                      <MFTextField
                        name={'operatingExpenseInWords'}
                        label="Operating Expense *"
                        placeholder="Enter operating expense"
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6} md={4} mt={2}>
                    <UseRadioGroup
                      formLabel="Exit Load Applicable *"
                      name={`exitLoadApplicable`}
                      items={Object.keys(yesNoMaster).map((ele) => ({
                        label: yesNoMaster[ele],
                        value: ele,
                      }))}
                    />
                  </Grid>
                  {isFieldApplicable(values.exitLoadApplicable) && (
                    <Grid item xs={12} sm={6} md={4}>
                      <MFTextField
                        name={'exitLoadInWords'}
                        label="Exit Load *"
                        placeholder="Enter exit load"
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6} md={4} mt={2}>
                    <UseRadioGroup
                      formLabel="Investment Plan Applicable *"
                      name={`investmentPlanApplicable`}
                      items={Object.keys(yesNoMaster).map((ele) => ({
                        label: yesNoMaster[ele],
                        value: ele,
                      }))}
                    />
                  </Grid>
                  {isFieldApplicable(values.investmentPlanApplicable) && (
                    <Grid item xs={12} sm={6} mt={2} md={4}>
                      <UseRadioGroup
                        formLabel="Investment Plan *"
                        name={`investmentPlanType`}
                        items={Object.keys(investmentPlanTypeMaster).map((ele) => ({
                          label: investmentPlanTypeMaster[ele],
                          value: ele,
                        }))}
                        value={values.investmentPlanType || ''}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField name="tcLink" label={`T&C Link`} placeholder={`Enter T&C Link`} />
                  </Grid>
                  {/* empty grid for proper alignment */}
                  <Grid xs={12}></Grid>
                  <Grid item xs={12} sm={6} sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex' }}>
                      <MFCheckbox name={`isApplicableForDirect`} label="Direct" />
                      <MFCheckbox
                        name={`isApplicableForDistributor`}
                        label="Distributor"
                        onChange={({ target: { checked } }) => {
                          setValues({
                            ...values,
                            isApplicableForDistributor: checked,
                            distributorIds: [],
                          });
                        }}
                        checked={values.isApplicableForDistributor}
                      />
                    </Box>
                    {values.isApplicableForDistributor && (
                      <MultipleSelect
                        name="distributorIds"
                        label="Applicable Distributor(s) *"
                        items={distributorList.map((item) => ({
                          key: item.id,
                          value: `${item.arnCode || 'N/A'} - ${item.name} (${item.type})`,
                        }))}
                        renderText="Selected Distributor(s)"
                      />
                    )}
                  </Grid>{' '}
                </Grid>
                {access && (
                  <Box sx={{ width: '100%', maxWidth: '350px', mx: 'auto', mt: 3 }}>
                    <MFSubmitButton label="Save" />
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </Formik>
    </>
  );
}
